import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { BaseFilterButton } from 'components/base-table/filters';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = { value: string; label: string };
type TInputs = { unit: TOption | undefined };

const defaultValues: TInputs = {
  unit: undefined,
};

export const CatalogUnitFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  // Update active filter button
  useEffect(() => {
    if (params.unit) setIsActive.on();
    else setIsActive.off();
  }, [params.unit]);

  const { control, handleSubmit, formState, reset } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update values
  const updateValues = () => {
    if (params.unit) {
      reset({ unit: { value: params.unit as string, label: params.unit as string } });
    } else {
      reset({ unit: undefined });
    }
  };

  useEffect(() => {
    updateValues();
  }, [params.unit]);

  const onSubmit = (data: TInputs) => {
    params.unit = (data.unit?.value as string) ?? '';

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const loadOptions = useCallback((inputValue: string, callback: (options: TOption[]) => void) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      http
        .get('ums/v1/supplier-intelligence/products/units', {
          params: { search: inputValue },
        })
        .then((res) => {
          const values: TOption[] =
            res?.data?.data?.map((item: any) => ({
              value: item?.name ?? item,
              label: item?.name ?? item,
            })) ?? [];

          callback(values);
        });
    }, 400);
  }, []);

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='unit'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Unit</FormLabel>
                <ChakraAsyncSelect
                  id='unit'
                  onChange={onChange}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                  defaultOptions
                  cacheOptions
                  loadOptions={loadOptions}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
